// Shared POS building blocks, in PettoBook's look: Nunito, soft pink accent
// (var(--c-primary) follows the shop theme), rounded cards, green = paid.
// Phone first; `useWide()` switches screens to a two-column iPad layout.
import { useEffect, useRef, useState } from "react";
import { zhEn } from "./posTier.js";
import { X, Minus, Plus, ScanLine } from "lucide-react";

export const C = {
  ink: "#4A4A68",
  sub: "#8A8A9E",
  faint: "#B4B4C6",
  line: "#F0EEF5",
  bg: "#FCFCFF",
  card: "#FFFFFF",
  accent: "var(--c-primary)",
  accentSoft: "var(--c-lighter)",
  grad: "linear-gradient(135deg,#FF8FA3,#FF5C8A)",
  green: "#10B981",
  greenSoft: "#ECFDF5",
  red: "#EF4444",
  redSoft: "#FEF2F2",
  amber: "#F59E0B",
  amberSoft: "#FFFBEB",
  blue: "#3B82F6",
  blueSoft: "#EFF6FF",
};

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const money = (n, sym = "$") => {
  const v = round2(n || 0);
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
  return (v < 0 ? "−" : "") + sym + s;
};

export function useWide(min = 900) {
  const get = () => typeof window !== "undefined" && window.innerWidth >= min;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    const on = () => setWide(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min]);
  return wide;
}

export const Btn = ({ children, kind = "primary", onClick, disabled, style, small, type = "button" }) => {
  const k = {
    primary: { background: C.grad, color: "#fff", boxShadow: "0 8px 20px rgba(255,92,138,0.28)", border: "none" },
    green: { background: C.green, color: "#fff", boxShadow: "0 8px 20px rgba(16,185,129,0.25)", border: "none" },
    soft: { background: C.accentSoft, color: C.accent, border: "none" },
    ghost: { background: "#fff", color: C.ink, border: "1.5px solid " + C.line },
    danger: { background: C.redSoft, color: C.red, border: "none" },
  }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...k, borderRadius: small ? 12 : 16, padding: small ? "8px 12px" : "14px 18px",
      fontSize: small ? 13 : 15, fontWeight: 900, fontFamily: "inherit", cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.45 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
      transition: "transform .12s", WebkitTapHighlightColor: "transparent", ...style,
    }}>{children}</button>
  );
};

export const Chip = ({ on, children, onClick, color, style }) => (
  <button type="button" onClick={onClick} style={{
    flexShrink: 0, padding: "7px 13px", borderRadius: 99, border: "none", cursor: "pointer", fontFamily: "inherit",
    fontSize: 13, fontWeight: 800, whiteSpace: "nowrap",
    background: on ? (color || C.accent) : "#F3F2F8", color: on ? "#fff" : C.ink, ...style,
  }}>{children}</button>
);

export const Pill = ({ children, tone = "grey", style }) => {
  const t = {
    green: [C.greenSoft, "#047857"], red: [C.redSoft, "#B91C1C"], amber: [C.amberSoft, "#B45309"],
    blue: [C.blueSoft, "#1D4ED8"], pink: [C.accentSoft, C.accent], grey: ["#F3F2F8", C.sub],
  }[tone];
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 900, background: t[0], color: t[1], whiteSpace: "nowrap", ...style }}>{children}</span>;
};

export const Seg = ({ value, options, onChange, style }) => (
  <div style={{ display: "flex", background: "#F3F2F8", borderRadius: 12, padding: 3, gap: 2, ...style }}>
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} style={{
        flex: 1, border: "none", borderRadius: 10, padding: "8px 6px", fontFamily: "inherit", fontSize: 13, fontWeight: 900, cursor: "pointer",
        background: value === v ? "#fff" : "transparent", color: value === v ? C.ink : C.sub,
        boxShadow: value === v ? "0 2px 8px rgba(74,74,104,0.10)" : "none",
      }}>{label}</button>
    ))}
  </div>
);

export const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{ background: C.card, borderRadius: 20, border: "1px solid " + C.line, boxShadow: "0 4px 16px rgba(74,74,104,0.05)", ...style, cursor: onClick ? "pointer" : style?.cursor }}>{children}</div>
);

export const Label = ({ children, style }) => <p style={{ fontSize: 12, fontWeight: 900, color: C.sub, margin: "0 0 6px", ...style }}>{children}</p>;

export const inputStyle = {
  width: "100%", boxSizing: "border-box", border: "1.5px solid " + C.line, borderRadius: 12, padding: "11px 12px",
  fontSize: 15, fontWeight: 700, color: C.ink, fontFamily: "inherit", outline: "none", background: "#fff",
};

export const Field = ({ label, hint, right, ...p }) => (
  <label style={{ display: "block", marginBottom: 12 }}>
    {label && <Label>{label}</Label>}
    <div style={{ position: "relative" }}>
      <input {...p} style={{ ...inputStyle, ...(right ? { paddingRight: 48 } : null), ...p.style }} />
      {right && <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)" }}>{right}</div>}
    </div>
    {hint && <p style={{ fontSize: 11, color: C.sub, fontWeight: 700, margin: "4px 2px 0" }}>{hint}</p>}
  </label>
);

export const Toggle = ({ on, onChange }) => (
  <button type="button" onClick={() => onChange(!on)} style={{ width: 46, height: 28, borderRadius: 99, border: "none", padding: 3, cursor: "pointer", background: on ? C.green : "#D9D8E3", flexShrink: 0, transition: "background .15s" }}>
    <span style={{ display: "block", width: 22, height: 22, borderRadius: 99, background: "#fff", transform: on ? "translateX(18px)" : "none", transition: "transform .15s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
  </button>
);

export const Stepper = ({ value, onChange, min = 0, max = Infinity, step = 1 }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#F3F2F8", borderRadius: 12, padding: 3 }}>
    <button type="button" onClick={() => onChange(Math.max(min, round2(value - step)))} disabled={value <= min} style={stepBtn}><Minus size={16} strokeWidth={3} /></button>
    <span style={{ minWidth: 34, textAlign: "center", fontSize: 16, fontWeight: 900, color: C.ink }}>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, round2(value + step)))} disabled={value >= max} style={stepBtn}><Plus size={16} strokeWidth={3} /></button>
  </div>
);
const stepBtn = { width: 34, height: 34, borderRadius: 10, border: "none", background: "#fff", color: C.ink, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };

// Bottom sheet on a phone, centred card on a tablet.
export const Sheet = ({ open, onClose, title, children, footer, wide: forceWide, z = 400, maxWidth = 560 }) => {
  const wide = useWide(700);
  useEffect(() => {
    if (!open) return;
    const k = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  const centred = wide || forceWide;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: z, background: "rgba(30,27,52,0.38)", display: "flex", alignItems: centred ? "center" : "flex-end", justifyContent: "center", animation: "posFade .15s" }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.bg, width: "100%", maxWidth: centred ? maxWidth : "100%", maxHeight: centred ? "88vh" : "92vh",
        borderRadius: centred ? 26 : "26px 26px 0 0", display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 -10px 40px rgba(30,27,52,0.18)", animation: centred ? "posPop .18s" : "posUp .22s",
        paddingBottom: centred ? 0 : "env(safe-area-inset-bottom,0px)",
      }}>
        {!centred && <div style={{ width: 40, height: 5, borderRadius: 99, background: "#E2E0EA", margin: "8px auto 0" }} />}
        {title !== undefined && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px 8px" }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 18, fontWeight: 900, color: C.ink }}>{title}</div>
            <button type="button" onClick={onClose} aria-label={zhEn("關閉", "Close")} style={{ width: 34, height: 34, borderRadius: 99, border: "none", background: "#F3F2F8", color: C.sub, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><X size={18} strokeWidth={2.6} /></button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 18px 18px", WebkitOverflowScrolling: "touch" }}>{children}</div>
        {footer && <div style={{ padding: "12px 18px 14px", borderTop: "1px solid " + C.line, background: "#fff" }}>{footer}</div>}
      </div>
    </div>
  );
};

export const Empty = ({ icon, title, sub, action }) => (
  <div style={{ textAlign: "center", padding: "48px 20px", color: C.sub }}>
    <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
    <p style={{ fontSize: 15, fontWeight: 900, color: C.ink, margin: "0 0 4px" }}>{title}</p>
    {sub && <p style={{ fontSize: 13, fontWeight: 700, margin: "0 0 14px" }}>{sub}</p>}
    {action}
  </div>
);

export const PosStyles = () => (
  <style>{`
    @keyframes posFade { from { opacity: 0 } to { opacity: 1 } }
    @keyframes posUp { from { transform: translateY(40px); opacity: .6 } to { transform: none; opacity: 1 } }
    @keyframes posPop { from { transform: scale(.96); opacity: 0 } to { transform: none; opacity: 1 } }
    @keyframes posScan { 0%,100% { top: 15% } 50% { top: 85% } }
    .pos-noscroll::-webkit-scrollbar { display: none }
    .pos-noscroll { scrollbar-width: none }
    .pos-tap:active { transform: scale(.97) }
  `}</style>
);

// Short beep for a successful scan (WebAudio, no asset).
let _ac = null;
export function beep(ok = true) {
  try {
    _ac = _ac || new (window.AudioContext || window.webkitAudioContext)();
    if (_ac.state === "suspended") _ac.resume();
    const o = _ac.createOscillator(), g = _ac.createGain();
    o.type = "square"; o.frequency.value = ok ? 2400 : 320;
    g.gain.value = 0.08; o.connect(g); g.connect(_ac.destination);
    o.start(); o.stop(_ac.currentTime + (ok ? 0.09 : 0.25));
  } catch { /* no audio */ }
}

// Camera barcode scanner (ZXing, lazy-loaded — same decoder settings as PetPOS).
// continuous: keeps scanning and calls onCode for each new code; otherwise
// closes after the first code.
export const CameraScanner = ({ open, onClose, onCode, continuous = true, title = zhEn("掃描條碼", "Scan barcode"), status }) => {
  const videoRef = useRef(null);
  const ctrlRef = useRef(null);
  const lastRef = useRef({ code: "", at: 0 });
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCount(0); setErr("");
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const { DecodeHintType, BarcodeFormat } = await import("@zxing/library");
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 30 });
        if (cancelled || !videoRef.current) return;
        const controls = await reader.decodeFromConstraints({
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        }, videoRef.current, (result) => {
          if (!result) return;
          const code = result.getText()?.trim();
          if (!code) return;
          const now = Date.now();
          if (code === lastRef.current.code && now - lastRef.current.at < 1200) return;
          lastRef.current = { code, at: now };
          beep(true);
          try { navigator.vibrate?.(30); } catch { /* none */ }
          setCount(c => c + 1);
          onCodeRef.current?.(code);
          if (!continuous) setTimeout(() => onClose?.(), 250);
        });
        if (cancelled) { controls.stop(); return; }
        ctrlRef.current = controls;
      } catch (e) {
        setErr((e?.name === "NotAllowedError" ? zhEn("未允許使用相機。請到系統設定開啟相機權限。", "Camera access isn't allowed. Turn on camera permission in the system settings.") : zhEn("相機開啟失敗：", "Couldn't open the camera: ")) + (e?.name === "NotAllowedError" ? "" : (e?.message || e)));
      }
    })();
    return () => { cancelled = true; try { ctrlRef.current?.stop(); } catch { /* */ } ctrlRef.current = null; };
  }, [open, continuous, onClose]);

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(10,10,14,0.92)", display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top,0px)", paddingBottom: "env(safe-area-inset-bottom,0px)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", color: "#fff" }}>
        <ScanLine size={22} color="#FF8FA3" />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 900, fontSize: 16 }}>{title}</p>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 12, opacity: 0.6 }}>{continuous ? zhEn("連續模式 · 已掃 ", "Continuous mode · scanned ") + count + zhEn(" 件", " items") : zhEn("對準條碼即可", "Point at a barcode")}</p>
        </div>
        <button type="button" onClick={onClose} style={{ border: "none", borderRadius: 99, padding: "9px 16px", background: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 900, fontSize: 14, fontFamily: "inherit", cursor: "pointer" }}>{zhEn("完成", "Done")}</button>
      </div>
      <div style={{ position: "relative", flex: 1, margin: "0 12px", borderRadius: 24, overflow: "hidden", background: "#000", maxHeight: 560 }}>
        <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", left: "12%", right: "12%", top: "28%", bottom: "28%", border: "3px solid rgba(255,143,163,0.9)", borderRadius: 18, boxShadow: "0 0 0 2000px rgba(0,0,0,0.25)" }}>
          <div style={{ position: "absolute", left: 8, right: 8, height: 2, background: "linear-gradient(90deg,transparent,#FF8FA3,transparent)", animation: "posScan 2s ease-in-out infinite" }} />
        </div>
        {(status || err) && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 18, textAlign: "center" }}>
            <span style={{ background: err ? "rgba(239,68,68,0.9)" : "rgba(0,0,0,0.6)", color: "#fff", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: 13 }}>{err || status}</span>
          </div>
        )}
      </div>
      <div style={{ height: 16 }} />
    </div>
  );
};
